import { useEffect, useRef, useState } from 'react'

/**
 * "A new version is ready" — the fix for an installed home-screen app
 * quietly serving an old build forever.
 *
 * index.html is `Cache-Control: max-age=0, must-revalidate`, so an ordinary
 * page load always gets the latest. The problem is upstream of caching: on
 * iOS, an app added to the home screen keeps its WebView process alive across
 * backgrounding, sometimes for days, and iOS only re-fetches when that
 * process is actually relaunched. A deploy can ship correctly — verified in
 * the live bundle, not assumed — and someone using the home-screen icon still
 * runs the JS that was loaded the last time they force-quit it.
 *
 * Checks on the same pageshow/visibilitychange moments the player already
 * wakes the audio graph on: whenever the tab becomes the one someone is
 * looking at again. A HEAD-weight fetch of index.html, compared by the
 * built script tag's src — that filename is content-hashed, so any real code
 * change changes it. No polling while the app is open and in front of
 * someone; checking only on return covers the case this exists for without
 * adding a background timer that does nothing for the common case of one
 * continuous session.
 *
 * Reload is a click, never automatic. Firing it silently mid-typing, mid-sign,
 * or mid-playback would trade a stale build for a lost one.
 */
export default function UpdateBanner() {
  const [available, setAvailable] = useState(false)
  // `script[type="module"]` alone is enough in the built app — it ships
  // exactly one. In `npm run dev`, Vite's own HMR preamble is an earlier
  // inline module with no src, which the bare selector matches instead
  // — hence the `[src]` qualifier, so this only ever compares real bundles.
  const currentSrc = useRef(
    typeof document !== 'undefined'
      ? document.querySelector('script[type="module"][src]')?.getAttribute('src')
      : null,
  )
  const checking = useRef(false)

  useEffect(() => {
    if (!currentSrc.current) return

    const check = async () => {
      if (checking.current || document.visibilityState !== 'visible') return
      checking.current = true
      try {
        const res = await fetch('/', { cache: 'no-store' })
        const html = await res.text()
        // DOMParser rather than a hand-rolled regex on the raw HTML — attribute
        // order in a script tag isn't guaranteed, and this only has to stay
        // exactly as correct as the querySelector used for currentSrc.
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const freshSrc = doc.querySelector('script[type="module"][src]')?.getAttribute('src')
        if (freshSrc && freshSrc !== currentSrc.current) setAvailable(true)
      } catch {
        // Offline or a flaky request. Silence here is correct — this is a
        // courtesy check, not something that should ever surface an error.
      } finally {
        checking.current = false
      }
    }

    check()
    document.addEventListener('visibilitychange', check)
    window.addEventListener('pageshow', check)
    return () => {
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('pageshow', check)
    }
  }, [])

  if (!available) return null

  return (
    <div className="updatebar" role="status">
      <span>A new version of Beseter is ready.</span>
      <button className="updatebar-btn" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  )
}
